//./src/pages/login.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import '../styles/login.css';
import { forgotPassword, loginUser, getGoogleLoginUrl } from "../services/login";
import { getMe } from "../services/session";
import { Eye, EyeOff } from "lucide-react";
import {
    API_BASE_CLIENT,
    STORAGE_KEYS,
    getDefaultApiBaseClient,
    resetApiBaseClient,
    setApiBaseClient,
    shouldAllowServerSelection,
} from "../config/vars";
import { clearStoredAuthStateForServerChange } from "../utils/clientStorage";

const SERVER_UNLOCK_KEY = "WR_CLIENT_SERVER_SELECTION_UNLOCKED";
const SECRET_TAP_TARGET = 7;
const SECRET_TAP_WINDOW_MS = 2500;

function formatServerLabel(value) {
    if (!value) return "не налаштовано";
    try {
        return new URL(value).host;
    } catch (_) {
        return value;
    }
}

const LoginPageClients = () => {
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [serverAccessUnlocked, setServerAccessUnlocked] = useState(() => {
        try {
            return localStorage.getItem(SERVER_UNLOCK_KEY) === "1";
        } catch (_) {
            return false;
        }
    });
    const [serverPanelOpen, setServerPanelOpen] = useState(false);
    const [serverUrl, setServerUrl] = useState(API_BASE_CLIENT || getDefaultApiBaseClient());
    const [serverMessage, setServerMessage] = useState("");
    const secretTapCountRef = useRef(0);
    const secretTapTimerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const serverSelectionEnabled = shouldAllowServerSelection();
    const defaultServerUrl = getDefaultApiBaseClient();
    const activeServerLabel = formatServerLabel(API_BASE_CLIENT || defaultServerUrl);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const deviceIdFromUrl = params.get("device_id");
        if (deviceIdFromUrl) {
            localStorage.setItem("deviceId", deviceIdFromUrl);
        }

        // Якщо cookies вже встановлені (в т.ч. після Google callback) — переходимо на /main.
        (async () => {
            const me = await getMe();
            if (me && me.user_type === "client") {
                // Persist session claims for other pages (news badges, WS channels, etc.)
                try {
                    if (me.client_id) localStorage.setItem("clientId", me.client_id);
                    if (me.name) localStorage.setItem("clientName", me.name);
                    if (me.email) localStorage.setItem("emailForGoogle", me.email);
                    if (me.location_id !== undefined) localStorage.setItem("locationId", me.location_id || "");
                    if (me.network_id !== undefined) localStorage.setItem("networkId", me.network_id || "");
                    if (me.active_context_id !== undefined && me.active_context_id !== null) {
                        localStorage.setItem(STORAGE_KEYS.ACTIVE_CLIENT_CONTEXT_ID, me.active_context_id);
                    }
                    const forceFlag = me.force_password_change ? "true" : "false";
                    localStorage.setItem("forcePasswordChange", forceFlag);
                } catch (_) {
                    // ignore
                }

                const deviceId = localStorage.getItem("deviceId");
                navigate(deviceId ? `/main?device_id=${deviceId}` : "/main", { replace: true });
            }
        })();
    }, [location, navigate]);

    useEffect(() => () => {
        if (secretTapTimerRef.current) {
            window.clearTimeout(secretTapTimerRef.current);
        }
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const reloadWithNewServer = () => {
        clearStoredAuthStateForServerChange();
        window.location.reload();
    };

    const handleSaveServer = () => {
        try {
            setApiBaseClient(serverUrl);
            reloadWithNewServer();
        } catch (error) {
            setServerMessage(error.message || "Не вдалося зберегти адресу сервера");
        }
    };

    const handleResetServer = () => {
        resetApiBaseClient();
        setServerUrl(defaultServerUrl);
        reloadWithNewServer();
    };

    const handleToggleServerPanel = () => {
        setServerPanelOpen((prev) => {
            const next = !prev;
            if (next) {
                setServerMessage("");
            }
            return next;
        });
    };

    const handleSecretTap = () => {
        if (!serverSelectionEnabled || serverAccessUnlocked) return;

        secretTapCountRef.current += 1;
        if (secretTapTimerRef.current) {
            window.clearTimeout(secretTapTimerRef.current);
        }

        secretTapTimerRef.current = window.setTimeout(() => {
            secretTapCountRef.current = 0;
        }, SECRET_TAP_WINDOW_MS);

        if (secretTapCountRef.current < SECRET_TAP_TARGET) {
            return;
        }

        secretTapCountRef.current = 0;
        window.clearTimeout(secretTapTimerRef.current);
        secretTapTimerRef.current = null;
        try {
            localStorage.setItem(SERVER_UNLOCK_KEY, "1");
        } catch (_) {
            // ignore
        }
        setServerAccessUnlocked(true);
        setServerPanelOpen(true);
        setServerMessage("Меню сервера розблоковано на цьому пристрої");
    };

    const handleLogin = async () => {
        setLoading(true);
        try {
            const { ok, data } = await loginUser(form);
            if (ok) {
                const me = await getMe();
                if (me) {
                    if (me.client_id) localStorage.setItem("clientId", me.client_id);
                    if (me.name) localStorage.setItem("clientName", me.name);
                    if (me.email) localStorage.setItem("emailForGoogle", me.email);
                    if (me.location_id !== undefined) localStorage.setItem("locationId", me.location_id || "");
                    if (me.network_id !== undefined) localStorage.setItem("networkId", me.network_id || "");
                    if (me.active_context_id !== undefined && me.active_context_id !== null) {
                        localStorage.setItem(STORAGE_KEYS.ACTIVE_CLIENT_CONTEXT_ID, me.active_context_id);
                    }
                    const forceFlag = me.force_password_change ? "true" : "false";
                    localStorage.setItem("forcePasswordChange", forceFlag);

                    const deviceId = localStorage.getItem("deviceId");
                    navigate(deviceId ? `/main?device_id=${deviceId}` : "/main");
                } else {
                    setMessage("❌ Не вдалося підтвердити сесію");
                }
            } else {
                setMessage("❌ " + (data.detail || "Помилка входу"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!form.email) {
            setMessage("❌ Введіть email перед відновленням пароля");
            return;
        }

        setLoading(true);
        try {
            const data = await forgotPassword(form.email);
            setMessage(data.message || "Перевірте пошту для відновлення пароля");
        } catch (error) {
            setMessage(`❌ ${error.message || "Помилка відновлення пароля"}`);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleRedirect = () => {
        window.location.href = getGoogleLoginUrl();
    };

    return (
        <div className="login-container">
            <>
                <h2 onClick={handleSecretTap}>Вхід</h2>
                {serverSelectionEnabled && serverAccessUnlocked ? (
                    <div className="server-config-card">
                        <button type="button" className="server-config-toggle" onClick={handleToggleServerPanel}>
                            Сервер: {activeServerLabel}
                        </button>
                        {serverPanelOpen ? (
                            <div className="server-config-panel">
                                <label htmlFor="client-server-url">API сервер</label>
                                <input
                                    id="client-server-url"
                                    type="url"
                                    value={serverUrl}
                                    onChange={(e) => setServerUrl(e.target.value)}
                                    placeholder="https://api.example.com"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                />
                                <div className="server-config-actions">
                                    <button type="button" onClick={handleSaveServer} disabled={loading}>
                                        Зберегти сервер
                                    </button>
                                    <button type="button" className="forgot-btn" onClick={handleResetServer} disabled={loading}>
                                        Сервер за замовчуванням
                                    </button>
                                </div>
                                <small>За замовчуванням: {formatServerLabel(defaultServerUrl)}</small>
                                {serverMessage ? <p className="server-config-message">{serverMessage}</p> : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div className="form-group">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={handleChange}
                    />
                    <div className="password-input-wrapper">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Пароль"
                            value={form.password}
                            onChange={handleChange}
                        />
                        <button
                            type="button"
                            className="toggle-password-btn"
                            onClick={() => setShowPassword((v) => !v)}
                            title={showPassword ? "Приховати пароль" : "Показати пароль"}
                            aria-label={showPassword ? "Приховати пароль" : "Показати пароль"}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                </div>
                <div className="button-group">
                    <button onClick={handleLogin} disabled={loading}>{loading ? "Зачекайте..." : "Увійти"}</button>
                    <button onClick={handleGoogleRedirect} disabled={loading}>Google-вхід</button>
                    <button className="forgot-btn" onClick={() => navigate("/auth")} disabled={loading}>Створити акаунт</button>
                    <button className="forgot-btn" onClick={handleForgotPassword} disabled={loading}>Забули пароль?</button>
                </div>
            </>
            {message && <p>{message}</p>}
        </div>
    );
};

export default LoginPageClients;
